import { useState, useRef, useEffect } from "react";
import { Lightbulb } from "@phosphor-icons/react";

/**
 * Botão discreto de dica/ajuda. Mostra o texto num tooltip ao passar o mouse
 * (ou clicar/tocar em dispositivos touch). Substitui parágrafos de instrução
 * espalhados pelo sistema para poluir menos as telas.
 *
 * Uso: <HintButton text="Explicação da tela..." />
 * Opcional: `size` (default 14), `className` extras.
 */
export default function HintButton({ text, size = 14, className = "" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  if (!text) return null;

  return (
    <span
      ref={ref}
      className={`relative inline-flex items-center ${className}`}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        className="text-yellow-600 hover:text-yellow-700 transition-colors p-0.5"
        aria-label="Ver dica"
        title="Ver dica"
        data-testid="hint-btn"
      >
        <Lightbulb size={size} weight="fill" />
      </button>
      {open && (
        <span
          className="absolute z-40 top-full left-0 mt-1 w-80 max-w-[80vw] bg-black text-white text-[11px] leading-relaxed px-3 py-2 shadow-lg border border-yellow-600 pointer-events-none"
          role="tooltip"
          data-testid="hint-tooltip"
        >
          {text}
        </span>
      )}
    </span>
  );
}
